/**
 * Tennis Stringing Async Data Loader
 * 
 * This script loads tennis stringing data asynchronously from a dedicated
 * page endpoint. This prevents blocking the initial page load.
 * 
 * @version 3.1.0
 * @updated 2025-01-08 - Added out-of-stock variant skip logic
 */

class TennisStringingLoader {
  constructor() {
    this.isLoading = false;
    this.isLoaded = false;
    this.data = null;
    this.error = null;
    this.callbacks = [];
    this.retryCount = 0;
    this.maxRetries = 2;
    this.timeout = 30000; // 30 seconds (metaobject processing takes 6-7s)
  }

  /**
   * Load stringing data asynchronously from dedicated page endpoint
   * @param {string} weightCategory - 'light' or 'heavy'
   * @param {string} productHandle - Product handle (not used, for future)
   * @returns {Promise<Object>} Stringing data
   */
  async load(weightCategory = 'heavy', productHandle = '') {
    // CRITICAL: Check if loading should be skipped (out of stock variant)
    if (window.TENNIS_STRINGING_SKIP_INIT === true) {
      throw new Error('Data loading skipped for out of stock variant');
    }
    
    // Validate inputs
    if (typeof weightCategory !== 'string' || !['light', 'heavy'].includes(weightCategory)) {
      console.warn('⚠️ Invalid weight category, defaulting to heavy:', weightCategory);
      weightCategory = 'heavy';
    }

    // Return cached data if already loaded
    if (this.isLoaded && this.data) {
      return this.data;
    }

    // Return existing promise if already loading
    if (this.isLoading) {
      return new Promise((resolve, reject) => {
        this.callbacks.push({ resolve, reject });
      });
    }

    this.isLoading = true;

    try {
      // Build URL to dedicated page endpoint using alternate view
      const apiUrl = `/pages/tennis-stringing-api-${encodeURIComponent(weightCategory)}`;
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/html, */*',  // Request HTML to trigger template rendering
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get response text
        const text = await response.text();
        
        if (!text || text.trim().length === 0) {
          throw new Error('Empty response received');
        }

        // Find JSON in response (might have HTML wrapper)
        const jsonStartIndex = text.indexOf('{');
        const jsonEndIndex = text.lastIndexOf('}');
        
        if (jsonStartIndex === -1 || jsonEndIndex === -1 || jsonEndIndex <= jsonStartIndex) {
          console.error('❌ No JSON found in response');
          console.error('Response preview:', text.substring(0, 500));
          throw new Error('Could not find JSON data in response');
        }

        const jsonStr = text.substring(jsonStartIndex, jsonEndIndex + 1);

        // Clean JSON: remove trailing commas
        const cleanedJson = jsonStr.replace(/,(\s*[\]}])/g, '$1');
        
        // Parse JSON
        let jsonData;
        try {
          jsonData = JSON.parse(cleanedJson);
        } catch (parseError) {
          console.error('❌ JSON parse error:', parseError);
          console.error('JSON preview:', cleanedJson.substring(0, 500));
          throw new Error('Invalid JSON: ' + parseError.message);
        }

        // Validate data structure
        if (!jsonData || typeof jsonData !== 'object') {
          throw new Error('Invalid data structure: not an object');
        }

        if (!Array.isArray(jsonData.types)) {
          console.warn('⚠️ No types array in data, using empty array');
          jsonData.types = [];
        }

      this.data = jsonData;
      this.isLoaded = true;
      this.isLoading = false;
      this.retryCount = 0;

        // Resolve any pending callbacks
        if (this.callbacks && Array.isArray(this.callbacks)) {
          this.callbacks.forEach(cb => {
            if (cb && typeof cb.resolve === 'function') {
              cb.resolve(jsonData);
            }
          });
        }
        this.callbacks = [];

        return jsonData;

      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // Handle timeout specifically
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout after ' + (this.timeout / 1000) + ' seconds');
        }
        
        throw fetchError;
      }

    } catch (error) {
      console.error('❌ Error loading stringing data:', error);
      this.error = error;
      this.isLoading = false;

      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * this.retryCount));
        
        return this.load(weightCategory, productHandle);
      }

      // Reject any pending callbacks
      if (this.callbacks && Array.isArray(this.callbacks)) {
        this.callbacks.forEach(cb => {
          if (cb && typeof cb.reject === 'function') {
            cb.reject(error);
          }
        });
      }
      this.callbacks = [];

      throw error;
    }
  }

  /**
   * Transform loaded data into window variables for existing JS
   * @param {Object} data - Stringing data
   * @returns {boolean} - True if populated without critical errors, false otherwise.
   */
  populateWindowVariables(data) {
    if (!data || typeof data !== 'object') {
      console.warn('⚠️ Invalid data object provided');
      return false;
    }

    if (!data.types || !Array.isArray(data.types)) {
      console.warn('⚠️ No types array in data');
      return false;
    }

    let successCount = 0;
    let errorCount = 0;

    try {
      data.types.forEach((type, typeIndex) => {
        // Validate type object
        if (!type || typeof type !== 'object') {
          console.warn(`⚠️ Invalid type at index ${typeIndex}`);
          errorCount++;
          return;
        }

        // Validate type handle
        if (!type.handle || typeof type.handle !== 'string') {
          console.warn(`⚠️ Missing or invalid handle for type at index ${typeIndex}`);
          errorCount++;
          return;
        }

        // Validate brands array
        if (!type.brands || !Array.isArray(type.brands)) {
          console.warn(`⚠️ Invalid brands array for type: ${type.handle}`);
          window[type.handle] = [];
          errorCount++;
          return;
        }

        try {
          // Create type variable (e.g., window.comfortMultifilament)
          window[type.handle] = type.brands
            .filter(brand => brand && typeof brand === 'object' && brand.brand_handle)
            .map(brand => ({
              brand_handle: brand.brand_handle || '',
              brand_name: brand.brand_name || '',
              popular: brand.popular === true,
              auto_select: brand.auto_select === true
            }));

          // Create product arrays for each brand
          type.brands.forEach((brand, brandIndex) => {
            if (!brand || typeof brand !== 'object') {
              console.warn(`⚠️ Invalid brand at index ${brandIndex} for type ${type.handle}`);
              errorCount++;
              return;
            }

            if (!brand.brand_handle || typeof brand.brand_handle !== 'string') {
              console.warn(`⚠️ Missing brand_handle for brand at index ${brandIndex} in type ${type.handle}`);
              errorCount++;
              return;
            }

            const varName = brand.brand_handle + '_products';
            
            // Ensure products is an array
            if (!Array.isArray(brand.products)) {
              window[varName] = [];
              console.warn(`⚠️ ${varName}: no products array, using empty array`);
            } else {
              // Validate and filter products
              window[varName] = brand.products.filter(product => {
                return product && 
                       typeof product === 'object' && 
                       product.id && 
                       product.title;
              });
            }
            
            successCount++;
          });

        } catch (typeError) {
          console.error(`❌ Error processing type ${type.handle}:`, typeError);
          errorCount++;
        }
      });

      return errorCount === 0;

    } catch (error) {
      console.error('❌ Critical error populating window variables:', error);
      return false;
    }
  }

  /**
   * Show loading indicator
   */
  showLoadingState() {
    try {
      const stringingButton = document.getElementById('stringsSelected');
      if (stringingButton && stringingButton.nodeType === 1) {
        stringingButton.textContent = 'Loading stringing options...';
        stringingButton.disabled = true;
        if (stringingButton.classList) {
          stringingButton.classList.add('loading');
        }
      } else {
        console.warn('⚠️ stringsSelected button not found for loading state');
      }
      
      // Also disable auto-select button
      const autoSelectButton = document.getElementById('auto_select_stringing');
      if (autoSelectButton && autoSelectButton.nodeType === 1) {
        if (autoSelectButton.classList) {
          autoSelectButton.classList.add('disabled');
        }
        autoSelectButton.style.opacity = '0.5';
        autoSelectButton.style.pointerEvents = 'none';
      }
    } catch (error) {
      console.error('❌ Error showing loading state:', error);
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoadingState() {
    try {
      // Don't enable button if we're skipping initialization (out of stock)
      if (window.TENNIS_STRINGING_SKIP_INIT === true) {
        return;
      }
      
      const stringingButton = document.getElementById('stringsSelected');
      if (stringingButton && stringingButton.nodeType === 1) {
        stringingButton.textContent = 'Select Stringing Options - Required';
        stringingButton.disabled = false;
        if (stringingButton.classList) {
          stringingButton.classList.remove('loading');
          stringingButton.classList.remove('error');
        }
      } else {
        console.warn('⚠️ stringsSelected button not found for hide loading state');
      }
      
      // Enable auto-select button now that data is loaded
      const autoSelectButton = document.getElementById('auto_select_stringing');
      if (autoSelectButton && autoSelectButton.nodeType === 1) {
        if (autoSelectButton.classList) {
          autoSelectButton.classList.remove('disabled');
        }
        autoSelectButton.style.opacity = '1';
        autoSelectButton.style.pointerEvents = 'auto';
      }
    } catch (error) {
      console.error('❌ Error hiding loading state:', error);
    }
  }

  /**
   * Show error state
   */
  showErrorState(errorMessage = null) {
    try {
      // Don't enable button if we're skipping initialization (out of stock)
      if (window.TENNIS_STRINGING_SKIP_INIT === true) {
        return;
      }
      
      const stringingButton = document.getElementById('stringsSelected');
      if (stringingButton && stringingButton.nodeType === 1) {
        const message = errorMessage || 'Error loading options (click to retry)';
        stringingButton.textContent = message;
        stringingButton.disabled = false;
        if (stringingButton.classList) {
          stringingButton.classList.remove('loading');
          stringingButton.classList.add('error');
        }
        
        // Add click handler for retry
        stringingButton.addEventListener('click', () => {
                if (stringingButton.classList.contains('error')) {
                  this.reset(); // Reset loader state before retrying
            const config = window.TENNIS_STRINGING_CONFIG || {};
            this.initialize(config);
          }
        }, { once: true });
        
      } else {
        console.warn('⚠️ stringsSelected button not found for error state');
      }
      
      // Ensure auto-select button is enabled for retry
      const autoSelectButton = document.getElementById('auto_select_stringing');
      if (autoSelectButton && autoSelectButton.nodeType === 1) {
        if (autoSelectButton.classList) {
          autoSelectButton.classList.remove('disabled');
        }
        autoSelectButton.style.opacity = '1';
        autoSelectButton.style.pointerEvents = 'auto';
      }
    } catch (error) {
      console.error('❌ Error showing error state:', error);
    }
  }

  /**
   * Initialize async loading with comprehensive error handling
   * @param {Object} options - Configuration options
   */
  async initialize(options = {}) {
    try {
      // CRITICAL: Check if initialization should be skipped (out of stock variant)
      if (window.TENNIS_STRINGING_SKIP_INIT === true) {
        return;
      }
      
      // Validate options
      if (!options || typeof options !== 'object') {
        console.warn('⚠️ Invalid options provided, using defaults');
        options = {};
      }

      const {
        weightCategory = 'heavy',
        productHandle = '',
        onSuccess = null,
        onError = null
      } = options;

      // Validate callbacks
      if (onSuccess !== null && typeof onSuccess !== 'function') {
        console.warn('⚠️ onSuccess is not a function, ignoring');
      }
      if (onError !== null && typeof onError !== 'function') {
        console.warn('⚠️ onError is not a function, ignoring');
      }

      this.showLoadingState();

      try {
        const data = await this.load(weightCategory, productHandle);
        
        if (!data) {
          throw new Error('No data returned from load');
        }

        const populateSuccess = this.populateWindowVariables(data);
        
        if (!populateSuccess) {
          console.warn('⚠️ Some errors occurred while populating variables');
        }
        
        this.hideLoadingState();

        // Call success callback safely
        if (typeof onSuccess === 'function') {
          try {
            onSuccess(data);
          } catch (callbackError) {
            console.error('❌ Error in onSuccess callback:', callbackError);
          }
        }

        // Dispatch custom event for other scripts
        try {
          if (window.dispatchEvent && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tennis-stringing-loaded', {
              detail: { data, success: populateSuccess }
            }));
          }
        } catch (eventError) {
          console.error('❌ Error dispatching success event:', eventError);
        }

      } catch (error) {
        console.error('❌ Initialization failed:', error);
        
        const errorMessage = error.message || 'Unknown error';
        this.showErrorState('Error loading - Click to retry');

        // Call error callback safely
        if (typeof onError === 'function') {
          try {
            onError(error);
          } catch (callbackError) {
            console.error('❌ Error in onError callback:', callbackError);
          }
        }

        // Dispatch error event
        try {
          if (window.dispatchEvent && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tennis-stringing-error', {
              detail: { error: error.message || String(error) }
            }));
          }
        } catch (eventError) {
          console.error('❌ Error dispatching error event:', eventError);
        }
      }

    } catch (criticalError) {
      console.error('❌ Critical error in initialize:', criticalError);
      
      // Last resort: try to show error state
      try {
        this.showErrorState('Critical error - Please refresh page');
      } catch (e) {
        console.error('❌ Could not show error state:', e);
      }
    }
  }

  /**
   * Reset the loader state
   */
  reset() {
    this.isLoading = false;
    this.isLoaded = false;
    this.data = null;
    this.error = null;
    this.callbacks = [];
  }

  /**
   * Setup variant change handler
   * @param {Object} options - Configuration options
   */
  setupVariantChangeHandler(options = {}) {
    try {
      const {
        sectionId = '',
        defaultProductWeight = '',
        onVariantChange = null
      } = options;

      const self = this;
      let isProcessing = false;
      let debounceTimer = null;

      function handleVariantChange(event) {
        try {
          if (!event || !event.data || !event.data.variant) {
            return;
          }
          
          const variant = event.data.variant;
          
          // Debounce to prevent duplicate calls
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(function() {
            processVariantChange(variant);
          }, 50);
          
        } catch (error) {
          console.error('❌ Error handling variant change:', error);
        }
      }
      
      function processVariantChange(variant) {
        try {
          // Prevent duplicate processing
          if (isProcessing) {
            return;
          }
          
          isProcessing = true;
          
          // Check if variant is out of stock
          const isSoldOut = document.querySelector('.pdp_price:has(.price.price--sold-out)') !== null;
          
          // Get weight from variant metafields if available
          const variantElement = document.querySelector(`[data-variant-id="${variant.id}"]`);
          let weightString = defaultProductWeight || '';
          
          // Try to get weight from variant-specific source if available
          if (variantElement && variantElement.dataset && variantElement.dataset.weight) {
            weightString = variantElement.dataset.weight;
          }
          
          // Calculate weight category
          let weightCategory = 'heavy';
          try {
            const weightMatch = weightString.match(/(\d+)\s*g/);
            if (weightMatch && weightMatch[1]) {
              const weight = parseFloat(weightMatch[1]);
              if (!isNaN(weight) && weight > 0) {
                weightCategory = weight <= 290 ? 'light' : 'heavy';
              }
            }
          } catch (e) {
            // Weight parsing failed, use default
          }
          
          // Update sticky button's variant ID input
          const stickyVariantInput = document.querySelector('.product-sticky-id');
          if (stickyVariantInput && variant && variant.id) {
            stickyVariantInput.value = variant.id;
          }
          
          // Reset all stringing UI elements
          const stringsSelected = document.getElementById('stringsSelected');
          if (stringsSelected) {
            stringsSelected.textContent = 'Select Stringing Options - Required';
            stringsSelected.removeAttribute('data-selected-value');
          }
          
          const stringTypeSelected = document.getElementById('stringTypeSelected');
          if (stringTypeSelected) {
            stringTypeSelected.textContent = 'Select String Type';
            stringTypeSelected.removeAttribute('data-selected-value');
            stringTypeSelected.removeAttribute('data-selected-mid-tension');
          }
          
          const stringBrand = document.getElementById('string-brand');
          if (stringBrand) {
            stringBrand.textContent = 'Select Brand';
            stringBrand.removeAttribute('data-selected-value');
          }
          
          const stringSelected = document.getElementById('stringSelected');
          if (stringSelected) {
            stringSelected.textContent = 'Select String';
            stringSelected.removeAttribute('data-selected-value');
          }
          
          // Clear dropdowns
          const brandDropdown = document.getElementById('stringingbranddropdownMenu');
          if (brandDropdown) {
            brandDropdown.innerHTML = '';
          }
          
          const productDropdown = document.getElementById('dropdownMenu');
          if (productDropdown) {
            productDropdown.innerHTML = '';
          }
          
          // Clear tension selections
          const mainTensionBtn = document.getElementById('string-mains-btn');
          if (mainTensionBtn) {
            mainTensionBtn.textContent = 'Select Tension';
            mainTensionBtn.removeAttribute('data-selected-value');
          }
          
          const crossTensionBtn = document.getElementById('string-crosses-btn');
          if (crossTensionBtn) {
            crossTensionBtn.textContent = 'Select Tension';
            crossTensionBtn.removeAttribute('data-selected-value');
          }
          
          // Uncheck stringing checkbox
          const checkboxYes = document.getElementById('add-stringing-checkbox');
          if (checkboxYes) {
            checkboxYes.checked = false;
          }
          
          const checkboxNo = document.getElementById('unstrung-checkbox');
          if (checkboxNo) {
            checkboxNo.checked = false;
          }
          
          // Hide stringing options
          const checkboxDiv = document.getElementById('stringing-options-panel');
          if (checkboxDiv) {
            checkboxDiv.style.display = 'none';
          }
          
          // Handle button state based on stock status
          const submitButton = sectionId ? document.getElementById('ProductSubmitButton-' + sectionId) : null;
          const stickyButton = document.getElementById('ProductSubmitButton-Sticky');
          
          // If variant is out of stock, disable stringing dropdown and don't fetch data
          if (isSoldOut) {
            // Set skip flag to prevent auto-initialization
            window.TENNIS_STRINGING_SKIP_INIT = true;
            
            const stringsSelected = document.getElementById('stringsSelected');
            if (stringsSelected) {
              stringsSelected.disabled = true;
              stringsSelected.textContent = 'Select Stringing Options - Required';
            }
            
            // Don't touch the submit buttons - let Shopify's default mechanism handle "Sold out" text
            
            isProcessing = false; // Allow next change
            return; // Exit early - don't fetch data for out of stock variants
          }
          
          // If variant is in stock, ensure stringing dropdown is enabled and button shows correct text
          // Clear skip flag to allow initialization
          window.TENNIS_STRINGING_SKIP_INIT = false;
          
          const stringsSelectedBtn = document.getElementById('stringsSelected');
          if (stringsSelectedBtn) {
            stringsSelectedBtn.disabled = false;
          }
          
          // For in-stock variants, ensure both buttons are disabled and show proper text
          if (submitButton) {
            submitButton.disabled = true;
            const buttonSpan = submitButton.querySelector('span');
            if (buttonSpan) {
              buttonSpan.textContent = 'Add to cart';
            }
          }
          
          if (stickyButton) {
            stickyButton.disabled = true;
            stickyButton.setAttribute('aria-disabled', 'true');
            const stickySpan = stickyButton.querySelector('span');
            if (stickySpan) {
              stickySpan.textContent = 'Add to cart';
            }
          }
          
          // Call custom callback if provided
          if (typeof onVariantChange === 'function') {
            try {
              onVariantChange(variant, weightCategory);
            } catch (callbackError) {
              console.error('❌ Error in onVariantChange callback:', callbackError);
            }
          }
          
          // Reset and reload stringing data with new weight category
          self.reset();
          
          // Update config with new weight category
          if (window.TENNIS_STRINGING_CONFIG) {
            window.TENNIS_STRINGING_CONFIG.weightCategory = weightCategory;
            
            // Reload data and repopulate window variables
            setTimeout(function() {
              self.initialize(window.TENNIS_STRINGING_CONFIG);
              isProcessing = false; // Allow next change
            }, 100);
          } else {
            isProcessing = false; // Allow next change
          }
          
        } catch (error) {
          console.error('❌ Error processing variant change:', error);
          isProcessing = false; // Allow next change
        }
      }

      // Method 1: Subscribe to PUB_SUB_EVENTS (preferred)
      var pubSubSetup = false;
      
      function setupSubscription() {
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
          pubSubSetup = true;
          return true;
        }
        return false;
      }
      
      // Try immediately
      if (!setupSubscription()) {
        // Wait for DOMContentLoaded and try again
        document.addEventListener('DOMContentLoaded', function() {
          setTimeout(function() {
            setupSubscription();
          }, 500);
        });
      }
      
      // Method 2: Direct variant selector listeners (fallback - only if PUB_SUB fails)
      function setupVariantListeners() {
        // Only setup fallback if PUB_SUB didn't work
        if (pubSubSetup) {
          return;
        }
        
        // Listen to variant-selects custom element
        const variantSelects = document.querySelector('variant-selects');
        
        if (variantSelects) {
          variantSelects.addEventListener('change', function(e) {
            setTimeout(function() {
              const currentVariantInput = document.querySelector('[name="id"]');
              
              if (currentVariantInput) {
                const variantId = currentVariantInput.value;
                
                // Create fake event structure for handleVariantChange
                handleVariantChange({
                  data: {
                    variant: {
                      id: variantId
                    }
                  }
                });
              }
            }, 200);
          });
        }
      }
      
      // Setup fallback listeners when DOM is ready (only if PUB_SUB fails)
      setTimeout(function() {
        if (!pubSubSetup) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', setupVariantListeners);
          } else {
            setupVariantListeners();
          }
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Error setting up variant change handler:', error);
    }
  }
}

// Wrap everything in try-catch for safety
try {
  // Create global instance
  if (typeof window !== 'undefined') {
    window.TennisStringingLoader = window.TennisStringingLoader || new TennisStringingLoader();

    // Auto-initialize if configuration exists AND not explicitly skipped
    if (typeof window.TENNIS_STRINGING_CONFIG !== 'undefined' && !window.TENNIS_STRINGING_SKIP_INIT) {
      // Small delay to ensure DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          window.TennisStringingLoader.initialize(window.TENNIS_STRINGING_CONFIG);
        });
      } else {
        window.TennisStringingLoader.initialize(window.TENNIS_STRINGING_CONFIG);
      }
    }
  }
} catch (error) {
  console.error('❌ Critical error initializing TennisStringingLoader:', error);
}
